/**
 * Única fuente de verdad del estado de una Cita en el frontend.
 * Debe reflejar exactamente `EstadoCita`/`ESTADOS_CITA`/`TRANSICIONES_CITA`
 * en backend/src/database/entities/cita.entity.ts — si cambia uno, cambia el otro.
 */
export type EstadoCita =
  | 'programada'
  | 'confirmada'
  | 'espera'
  | 'en_curso'
  | 'completada'
  | 'cancelada'
  | 'no_asistio';

export const ESTADOS_CITA: EstadoCita[] = [
  'programada', 'confirmada', 'espera', 'en_curso', 'completada', 'cancelada', 'no_asistio',
];

/** Grafo de transiciones válidas — debe coincidir con TRANSICIONES_CITA del backend. */
export const TRANSICIONES_CITA: Record<EstadoCita, EstadoCita[]> = {
  programada: ['confirmada', 'cancelada', 'no_asistio'],
  confirmada: ['programada', 'espera', 'cancelada', 'no_asistio'],
  espera:     ['confirmada', 'en_curso', 'cancelada', 'no_asistio'],
  en_curso:   ['completada', 'cancelada'],
  completada: [],
  cancelada:  ['programada'],
  no_asistio: ['programada'],
};

export interface EstadoCitaInfo {
  label: string;
  /** Clases Tailwind para un badge/pill (fondo + texto + borde). */
  badgeCls: string;
  /** Clases Tailwind para fondos sólidos más planos (dashboards, tablas). */
  softCls: string;
  /** Color hex — usado en el calendario (puntos, bordes de evento). */
  hex: string;
  /** Clase del punto de color en el Kanban de Recepción. */
  dotCls: string;
}

export const ESTADO_CITA_INFO: Record<EstadoCita, EstadoCitaInfo> = {
  programada: {
    label: 'Por confirmar',
    badgeCls: 'bg-amber-50 text-amber-700 border-amber-200',
    softCls: 'bg-blue-50 text-blue-700',
    hex: '#3b82f6',
    dotCls: 'bg-amber-400',
  },
  confirmada: {
    label: 'Confirmada',
    badgeCls: 'bg-blue-50 text-blue-700 border-blue-200',
    softCls: 'bg-green-50 text-green-700',
    hex: '#22c55e',
    dotCls: 'bg-blue-400',
  },
  espera: {
    label: 'En sala de espera',
    badgeCls: 'bg-violet-50 text-violet-700 border-violet-200',
    softCls: 'bg-violet-50 text-violet-700',
    hex: '#8b5cf6',
    dotCls: 'bg-violet-400',
  },
  en_curso: {
    label: 'En consulta',
    badgeCls: 'bg-green-50 text-green-700 border-green-200',
    softCls: 'bg-amber-50 text-amber-700',
    hex: '#f59e0b',
    dotCls: 'bg-green-400',
  },
  completada: {
    label: 'Completada',
    badgeCls: 'bg-gray-100 text-gray-600 border-gray-200',
    softCls: 'bg-gray-100 text-gray-500',
    hex: '#9ca3af',
    dotCls: 'bg-gray-400',
  },
  cancelada: {
    label: 'Cancelada',
    badgeCls: 'bg-red-50 text-red-600 border-red-200',
    softCls: 'bg-red-50 text-red-600',
    hex: '#ef4444',
    dotCls: 'bg-red-400',
  },
  no_asistio: {
    label: 'No asistió',
    badgeCls: 'bg-orange-100 text-orange-600 border-orange-200',
    softCls: 'bg-orange-50 text-orange-700',
    hex: '#f97316',
    dotCls: 'bg-orange-400',
  },
};

/** Lookup con fallback seguro — nunca revienta si llega un estado desconocido. */
export function getEstadoCitaInfo(estado: string | undefined | null): EstadoCitaInfo {
  return (estado && ESTADO_CITA_INFO[estado as EstadoCita]) || {
    label: estado ?? '—',
    badgeCls: 'bg-gray-100 text-gray-600 border-gray-200',
    softCls: 'bg-gray-100 text-gray-500',
    hex: '#9ca3af',
    dotCls: 'bg-gray-400',
  };
}

/** Estados que ya no pueden avanzar por el flujo normal de recepción/agenda. */
export const ESTADOS_CITA_TERMINALES: EstadoCita[] = ['completada', 'cancelada', 'no_asistio'];

export function puedeTransicionar(desde: EstadoCita, hacia: EstadoCita): boolean {
  return (TRANSICIONES_CITA[desde] ?? []).includes(hacia);
}
